import {
  Add,
  Delete,
  DragIndicator,
  ExpandMore,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import FormRenderer from '../FormRenderer'
import { FieldDef } from '../types'

type Item = Record<string, unknown>

interface Props {
  field: FieldDef
  value: Item[]
  onChange: (next: Item[]) => void
}

function emptyItem(fields: FieldDef[]): Item {
  const out: Item = {}
  for (const f of fields) {
    if (f.type === 'list') out[f.id] = []
    else if (f.type === 'repeater') out[f.id] = []
    else if (f.type === 'numberRange') out[f.id] = { low: 0, high: 0, currency: 'USD' }
    else if (f.type === 'dimensions') out[f.id] = {}
    else if (f.type === 'trajectory') out[f.id] = []
    else if (f.type === 'number') out[f.id] = 0
    else out[f.id] = ''
  }
  return out
}

// Stop button clicks from toggling the accordion. Each button needs this
// individually because the row body is intentionally clickable for expand.
const stopAndRun =
  (fn: () => void) =>
  (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    fn()
  }

// Stable id for a repeater item — we use array index because items don't have
// natural keys. Drag-end remaps the array via arrayMove.
const itemKey = (i: number) => `item-${i}`

interface SortableItemProps {
  index: number
  item: Item
  subFields: FieldDef[]
  expanded: boolean
  canDelete: boolean
  summaryLabel: string
  onToggleExpand: (open: boolean) => void
  onChange: (next: Item) => void
  onRemove: () => void
}

function SortableItem({
  index,
  item,
  subFields,
  expanded,
  canDelete,
  summaryLabel,
  onToggleExpand,
  onChange,
  onRemove,
}: SortableItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: itemKey(index),
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.12)' : undefined,
  }

  return (
    <Accordion
      ref={setNodeRef}
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, open) => onToggleExpand(open)}
      style={style}
      sx={{
        border: 1,
        borderColor: expanded ? 'primary.light' : 'divider',
        '&:before': { display: 'none' },
        transition: 'border-color 150ms ease',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
          '&:hover': { background: 'action.hover' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <Box
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'text.secondary',
              cursor: isDragging ? 'grabbing' : 'grab',
              px: 0.25,
            }}
            aria-label="Drag to reorder"
          >
            <DragIndicator fontSize="small" />
          </Box>
          <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: 'text.primary' }}>
            {summaryLabel}
          </Typography>
          <IconButton
            size="small"
            onClick={stopAndRun(onRemove)}
            disabled={!canDelete}
            aria-label="Delete"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1 }}>
        <FormRenderer fields={subFields} values={item} onChange={onChange} />
      </AccordionDetails>
    </Accordion>
  )
}

export default function Repeater({ field, value, onChange }: Props) {
  const items = value || []
  const subFields = field.fields || []
  const min = field.min ?? 0
  const max = field.max ?? Infinity

  // Default the first item open so the AI-generated content is immediately
  // visible. Others start collapsed; user can toggle freely.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})
  const isExpanded = (i: number) => (expanded[i] !== undefined ? expanded[i] : i === 0)
  const setExpand = (i: number, open: boolean) =>
    setExpanded((prev) => ({ ...prev, [i]: open }))

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex((_, i) => itemKey(i) === active.id)
    const newIndex = items.findIndex((_, i) => itemKey(i) === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(items, oldIndex, newIndex))
    // Move the expanded state along with the row so the same logical item
    // stays expanded after the drag.
    setExpanded((prev) => {
      const next: Record<number, boolean> = {}
      const order = items.map((_, i) => i)
      const reordered = arrayMove(order, oldIndex, newIndex)
      reordered.forEach((origIdx, newIdx) => {
        if (prev[origIdx] !== undefined) next[newIdx] = prev[origIdx]
      })
      return next
    })
  }

  const updateItem = (i: number, item: Item) => {
    const next = [...items]
    next[i] = item
    onChange(next)
  }

  const remove = (i: number) => {
    onChange(items.filter((_, idx) => idx !== i))
    // Drop the expanded entry for that slot
    setExpanded((prev) => {
      const next: Record<number, boolean> = {}
      Object.keys(prev).forEach((k) => {
        const idx = Number(k)
        if (idx === i) return
        next[idx > i ? idx - 1 : idx] = prev[idx]
      })
      return next
    })
  }

  const add = () => {
    const newIdx = items.length
    onChange([...items, emptyItem(subFields)])
    setExpand(newIdx, true)
  }

  // Use the first field's value as the accordion summary label, if it's text/number
  const summaryLabel = (item: Item, idx: number) => {
    const firstField = subFields[0]
    if (firstField) {
      const v = item?.[firstField.id]
      if (typeof v === 'string' && v.trim()) return v
      if (typeof v === 'number') return String(v)
    }
    return `${field.label} #${idx + 1}`
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          {field.label} {min > 0 && `(min ${min})`} {max < Infinity && `(max ${max})`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {items.length} item{items.length === 1 ? '' : 's'}
        </Typography>
      </Stack>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((_, i) => itemKey(i))}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={1}>
            {items.map((item, i) => (
              <SortableItem
                key={itemKey(i)}
                index={i}
                item={item}
                subFields={subFields}
                expanded={isExpanded(i)}
                canDelete={items.length > min}
                summaryLabel={summaryLabel(item, i)}
                onToggleExpand={(open) => setExpand(i, open)}
                onChange={(next) => updateItem(i, next)}
                onRemove={() => remove(i)}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
      <Box>
        <Button onClick={add} startIcon={<Add />} size="small" disabled={items.length >= max}>
          Add {field.label.toLowerCase()}
        </Button>
      </Box>
    </Stack>
  )
}
